import {
  TPageableDataWithContentDB,
  TPageableDataWithContentUI,
  TTableFilterDataUI,
  TTableFilters,
} from './generic.types';

export type GetIoTransactionsParams = TTableFilters & {
  sourceId: string;
  sourceType?: TIoSourceType;
};

// Source type for Input/Output operations
export type TIoSourceType =
  | 'ORGANIZATION'
  | 'ELC'
  | 'PEOPLE'
  | 'EVENT'
  | 'WORKSHOP';

export type TCategoryTypeEnum = 'MONEY' | 'MATERIALS' | 'TIME';

export type TIoCategoryDb = {
  resourceId: string;
  categoryName: string;
  isAeyc: boolean;
};

export type TIoCategoryOption = {
  label: string;
  value: string;
  isAeyc: boolean;
};

// File upload types
export type TUploadIoDocumentUI = {
  file: Blob;
};

export type TDeleteIoDocumentUI = {
  fileId: string;
};

// Add bulk delete type to match the pattern from AddResourcesModal
export type TDeleteIoDocumentsUI = {
  files: TIoDocumentResponse[]; // Use the response type since payload matches response
};

export type TIoDocumentResponse = {
  name: string;
  url: string;
};

// Add the selected files type used in InputOutputModal
export type TSelectedIoFiles = {
  name: string;
  url: string;
  uid: string; // Only for UI tracking
};

// Fields for an output logged at an event the ELC attended but did not host,
// and the self-reported families served of a Public Distribution (GROW-4257)
export type TExternalEventFields = {
  isExternalEvent?: boolean;
  externalEventName?: string | null;
  externalEventHost?: string | null;
  externalEventZip?: string | null;
  familiesServed?: number | null;
};

// Add the new types for createInputOutput API call
export type TCreateInputOutputRequest = TExternalEventFields & {
  direction: 'GIVER' | 'RECEIVER';
  sourceType: TIoSourceType;
  sourceId: string;
  ioDate: string; // Format: MM/DD/YYYY
  giverType: 'HOST' | 'LOCATION' | 'PEOPLE' | 'ELC' | 'ORGANIZATION';
  giverId: string;
  receiverType: TIoReceiverEntityType;
  // Absent for a Public Distribution receiver, which names no recipient
  receiverId?: string;
  type: 'MONEY' | 'MATERIALS' | 'TIME';
  categoryId: string | null;
  value: string | null;
  notes: string | null;
  hostOrLocation?: 'HOST' | 'LOCATION';
  document?: {
    url: string;
    name: string;
  };
  isInkindDonation?: boolean;
  inkindDollarValue?: number;
  timeHours?: number;
  timeMinutes?: number;
};

export type TCreateInputOutputResponse = {
  id: string;
  message?: string;
};

export type TIoEntityType =
  | 'HOST'
  | 'LOCATION'
  | 'PEOPLE'
  | 'ELC'
  | 'ORGANIZATION';

// "Handed to the general public": a receiver with no recipient record. Only
// ever a receiver, never a giver (GROW-4257 D-3).
export const PUBLIC_DISTRIBUTION = 'PUBLIC_DISTRIBUTION';

export type TIoReceiverEntityType = TIoEntityType | typeof PUBLIC_DISTRIBUTION;

export type TUpdateInputOutputRequest = TExternalEventFields & {
  ioDate?: string;
  giverType?: TIoEntityType;
  giverId?: string;
  receiverType?: TIoReceiverEntityType;
  receiverId?: string;
  type?: 'MONEY' | 'MATERIALS' | 'TIME';
  categoryId?: string;
  value?: string;
  notes?: string;
  isInkindDonation?: boolean;
  inkindDollarValue?: number;
  timeHours?: number;
  timeMinutes?: number;
  document?: {
    url: string;
    name: string;
  } | null;
};

// New types for IO transactions list with backend-provided filters
export type TIoTransactionsFiltersDB = {
  givers?: Record<string, string>;
  receivers?: Record<string, string>;
};

export type TIoTransactionsFiltersUI = {
  givers?: TTableFilterDataUI[];
  receivers?: TTableFilterDataUI[];
};

export type TIoTransactionsListDB = {
  data: TPageableDataWithContentDB<unknown[]>;
  filters?: TIoTransactionsFiltersDB;
};

// Input/Output table row types (moved from inputOutput.types.ts)
export type TInputOutputType = 'Money' | 'Material' | 'Materials' | 'Time';

export type TInputOutputRow = {
  id: string;
  date: string;
  giving: string;
  receiving: string;
  type: TInputOutputType;
  category: string;
  amount: string;
  notes: string;
  document: boolean;
  documentName?: string;
  documentUrl?: string;
  documentIsSvg?: boolean;
  giverId?: string;
  receiverId?: string;
  giverType?: TIoEntityType;
  receiverType?: TIoReceiverEntityType;
  categoryId?: string;
  rawType?: 'MONEY' | 'MATERIALS' | 'TIME';
  rawValue?: string | null;
  hostOrLocation?: 'HOST' | 'LOCATION';
  eventOrWorkshopName?: string;
  canEdit?: boolean;
  isInkindDonation?: boolean;
  inkindDollarValue?: number | null;
  timeHours?: number | null;
  timeMinutes?: number | null;
  isExternalEvent?: boolean;
  externalEventName?: string | null;
  externalEventHost?: string | null;
  externalEventZip?: string | null;
  familiesServed?: number | null;
};

export type TIoTransactionsListUI = TPageableDataWithContentUI<
  TInputOutputRow[]
> & {
  filters?: TIoTransactionsFiltersUI;
};
